import { useEffect, type RefObject } from 'react'

/**
 * Closes a popover when the pointer goes down elsewhere or Escape is pressed. Skips the
 * listeners entirely while `enabled` is false so closed popovers cost nothing.
 */
export const useClickOutside = (
  ref: RefObject<HTMLElement | null>,
  onDismiss: () => void,
  enabled = true,
) => {
  useEffect(() => {
    if (!enabled) return

    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onDismiss()
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [enabled, onDismiss, ref])
}
